import { HttpInterceptorFn, HttpErrorResponse, HttpRequest, HttpHandlerFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, catchError, filter, from, switchMap, take, throwError } from 'rxjs';

import { environment } from '../../../../../apps/gs-2.0/src/environments/environment';
import { AuthService } from '../services/auth.service';

// Module-level state to coordinate concurrent 401 handling
let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

function buildHeaders(req: HttpRequest<unknown>, tenantId: string, token?: string | null) {
  let headers = req.headers
    .set('Access-Control-Allow-Origin', '*')
    .set('Cache-Control', 'no-cache')
    .set('Pragma', 'no-cache')
    .set('tenantId', tenantId);

  if (token) {
    headers = headers.set('Authorization', `Bearer ${token}`);
  }

  return headers;
}

function handle401Error(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthService,
  tenantId: string
): Observable<any> {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    // Capture before the refresh attempt since the token may be cleared on failure.
    const hadRefreshToken = authService.hasRefreshToken();

    return from(authService.refreshToken()).pipe(
      switchMap(() => {
        isRefreshing = false;
        const newToken = authService.getAccessToken();
        refreshTokenSubject.next(newToken);
        return next(req.clone({ headers: buildHeaders(req, tenantId, newToken) }));
      }),
      catchError(() => {
        isRefreshing = false;
        // Only treat refresh failure as session expiry if the user actually had a session.
        // If there was never a refresh token (e.g. unauthenticated user on a public page),
        // skip the logout/redirect so the page can load normally.
        if (hadRefreshToken) {
          authService.sessionExpiredLogout();
        }
        return EMPTY;
      })
    );
  }

  // A refresh is already in progress — wait for the new token, then retry
  return refreshTokenSubject.pipe(
    filter((token): token is string => token !== null),
    take(1),
    switchMap((token) => next(req.clone({ headers: buildHeaders(req, tenantId, token) })))
  );
}

export const httpInterceptor: HttpInterceptorFn = (req, next) => {
  // Bypass header setting for Keycloak issuer requests
  const keycloakIssuer = environment.keycloak?.issuer;
  const tenantId = environment.tenantId;

  if (keycloakIssuer && req.url.startsWith(keycloakIssuer)) {
    return next(req);
  }

  const authService = inject(AuthService);

  const token = (authService.hasValidToken() && !authService.guestMode) ? authService.getAccessToken() : null;
  const clonedRequest = req.clone({ headers: buildHeaders(req, tenantId, token) });

  return next(clonedRequest).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        // Attempt to refresh the token before logging out.
        // This handles the case where the token expires mid-transaction.
        return handle401Error(req, next, authService, tenantId);
      }
      return throwError(() => error);
    })
  );
};
